import type { VideoType } from "./room-state";

// Detects the video source type from a URL.
export function detectVideoType(url: string): VideoType {
  const trimmed = url.toLowerCase();
  if (
    trimmed.includes("youtube.com") ||
    trimmed.includes("youtu.be") ||
    trimmed.includes("youtube-nocookie.com")
  ) {
    return "youtube";
  }
  if (trimmed.includes(".m3u8") || trimmed.includes("/hls/") || trimmed.includes("application/x-mpegurl")) {
    return "hls";
  }
  if (trimmed.includes("twitch.tv")) {
    return "twitch";
  }
  if (trimmed.includes("vimeo.com")) {
    return "vimeo";
  }
  return "direct";
}

// Extracts the YouTube video id from any common YouTube URL shape.
export function extractYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.slice(1).split("/")[0] || null;
    }
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/watch")) {
        return u.searchParams.get("v");
      }
      const shortsMatch = u.pathname.match(/\/shorts\/([\w-]+)/);
      if (shortsMatch) return shortsMatch[1];
      const embedMatch = u.pathname.match(/\/embed\/([\w-]+)/);
      if (embedMatch) return embedMatch[1];
      if (u.pathname.startsWith("/live/")) {
        return u.pathname.split("/")[2] || null;
      }
    }
  } catch {
    // not a URL
  }
  return null;
}

export function formatTime(totalSecs: number): string {
  const s = Math.max(0, Math.floor(totalSecs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  }
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

// Parses a timestamp like "02:15" or "1:02:15" from chat text into seconds.
export function parseTimestamp(text: string): number | null {
  const match = text.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;
  if (match[3] !== undefined) {
    return parseInt(match[1], 10) * 3600 + parseInt(match[2], 10) * 60 + parseInt(match[3], 10);
  }
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

export function isTimestampInText(text: string): boolean {
  return /\b\d{1,2}:\d{2}\b/.test(text);
}
